import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function RegisterModal({ event, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", distance: "", tshirt_size: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!event) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.slug}/register`, form);
      if (data.payment_required) {
        const loaded = await loadRazorpay();
        if (!loaded) throw new Error("payment_sdk");
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "SPORTIVE",
          description: `${event.name} — ${form.distance}`,
          order_id: data.order_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#18A8DD" },
          modal: { ondismiss: () => toast.info("Payment cancelled — your registration is saved as unpaid. You can pay later from My Registration.") },
          handler: async (resp) => {
            try {
              await api.post("/payments/verify", { ...resp, reg_id: data.reg_id });
              setDone(data.reg_id);
              toast.success("Payment successful — registration confirmed!");
            } catch {
              toast.error("Payment verification failed. Contact us with your registration ID: " + data.reg_id);
            }
          },
        });
        rzp.open();
        return;
      }
      setDone(data.reg_id);
      toast.success("Registration received!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment failed to start. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-ink/80 backdrop-blur-sm" onClick={onClose} />
      <div className="card-k relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-7">
        <button data-testid="register-modal-close" onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
        {done ? (
          <div className="py-8 text-center">
            <p className="eyebrow">REGISTRATION RECEIVED</p>
            <h3 className="mt-3 font-display text-3xl font-bold text-white">You're on the list!</h3>
            <p className="mt-4 text-slate-400">Your registration ID for <span className="text-brand-cyan">{event.name}</span>:</p>
            <p data-testid="registration-id" className="mt-3 font-mono text-2xl font-semibold tracking-widest text-brand-olive">{done}</p>
            <p className="mt-4 text-sm text-slate-500">A confirmation email is on its way. Keep this ID for results & certificates.</p>
            <button data-testid="register-done-close" onClick={onClose} className="btn-kinetic btn-primary-k mt-7">Done</button>
          </div>
        ) : (
          <>
            <p className="eyebrow">REGISTER NOW</p>
            <h3 className="mt-2 font-display text-3xl font-bold text-white">{event.name}</h3>
            <p className="mt-1 text-sm text-slate-400">{event.date_text} • {event.venue}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input data-testid="reg-name" required placeholder="Full name" className="input-k" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="reg-email" required type="email" placeholder="Email" className="input-k" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="reg-phone" required placeholder="Phone / WhatsApp" className="input-k" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="reg-city" placeholder="City" className="input-k" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <select data-testid="reg-distance" required className="input-k" value={form.distance} onChange={(e) => setForm({ ...form, distance: e.target.value })}>
                  <option value="">Select distance</option>
                  {event.distances?.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <select data-testid="reg-tshirt" className="input-k" value={form.tshirt_size} onChange={(e) => setForm({ ...form, tshirt_size: e.target.value })}>
                <option value="">T-shirt size (optional)</option>
                {["XS", "S", "M", "L", "XL", "XXL"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea data-testid="reg-notes" rows={2} placeholder="Anything we should know? (optional)" className="input-k" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <button data-testid="reg-submit" disabled={busy} className="btn-kinetic btn-primary-k w-full justify-center disabled:opacity-60">
                {busy ? "Processing..." : (Number(event.entry_fee_amount) > 0 ? `Pay ₹${event.entry_fee_amount} & Register` : "Confirm Registration")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
